/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Loader } from "@nordcloud/gnui";
import { useCustomerConfigQuery } from "~/generated/graphql";
import { ERROR_TEXT } from "~/constants";
import { showError } from "~/services/toast";
import { includes } from "~/tools/array";
import { Currency } from "~/tools/money/currencies";
import { Maybe } from "~/tools/types";
import {
  checkCurrencyRateValidity,
  CurrencyLSKeys,
  kantorQuery,
} from "./utils";

type CustomerConfig = {
  currency: Currency;
  defaultCurrency?: Currency;
  isDefaultPriority?: boolean;
  riCoverageTarget?: number;
  enabledFeatures?: string[];
};

type CustomerConfigInterface = {
  config: Maybe<CustomerConfig>;
  update: (newConfig: CustomerConfig) => void;
};

export const CustomerConfigContext =
  React.createContext<CustomerConfigInterface>({
    config: null,
    update: () => {
      // do nothing.
    },
  });
CustomerConfigContext.displayName = "CustomerConfigContext";

type CustomerConfigProviderProps = {
  children: React.ReactNode;
};

type PersistedCurrency = {
  currency?: string;
  rate?: number;
};

function persistCurrencySettings(input: PersistedCurrency) {
  if (input.currency) {
    localStorage.setItem(CurrencyLSKeys.CURRENCY, input.currency);
  }
  if (input.rate && input.currency) {
    const currencyRate = {
      rate: input.rate,
      expirationDate: dayjs().add(1, "day"),
    };
    localStorage.setItem(
      `${CurrencyLSKeys.RATE}${input.currency}`,
      JSON.stringify(currencyRate)
    );
  }
}

export function CustomerConfigProvider({
  children,
}: CustomerConfigProviderProps) {
  const storedCurrency = localStorage.getItem(CurrencyLSKeys.CURRENCY);
  const [config, setConfig] = React.useState<CustomerConfig>({
    currency: setISOCurrencyFromString(storedCurrency),
    defaultCurrency: Currency.Eur,
    riCoverageTarget: 0,
    enabledFeatures: [],
  });

  const { data, loading } = useCustomerConfigQuery();

  const defaultCurrency = React.useMemo(() => {
    const customerConfigCurrency = data?.customerConfig?.defaultCurrency;
    return setISOCurrencyFromString(customerConfigCurrency);
  }, [data]);

  React.useEffect(() => {
    if (data?.customerConfig == null) {
      return;
    }

    const riCoverageTarget = data?.customerConfig?.riCoverageTarget || 0;
    const enabledFeatures = data?.customerConfig?.enabledFeatures || [];
    const isDefaultPriority = defaultCurrency !== Currency.Eur;

    // Bugfix: Bypass for formatMoney function.
    // For default currency we lock apply rate (when default currencies are set then lock apply rate).
    localStorage.setItem(
      CurrencyLSKeys.DEFAULT_PRIORITY,
      `${isDefaultPriority}`
    );

    setConfig({
      currency: isDefaultPriority
        ? defaultCurrency
        : setISOCurrencyFromString(storedCurrency),
      defaultCurrency,
      riCoverageTarget,
      enabledFeatures,
      isDefaultPriority,
    });
  }, [data]);

  const setCurrency = (input: PersistedCurrency) => {
    persistCurrencySettings(input);
    setConfig((prevConfig) => ({
      ...prevConfig,
      ...(input.currency != null && {
        currency: setISOCurrencyFromString(input.currency, prevConfig.currency),
      }),
    }));
  };

  React.useEffect(() => {
    async function getRates() {
      const isRateValid = checkCurrencyRateValidity(
        localStorage.getItem(`${CurrencyLSKeys.RATE}${config.currency}`)
      );
      if (!isRateValid) {
        try {
          const { exchangeRate } = await kantorQuery(config.currency);
          setCurrency({
            rate: exchangeRate,
            currency: config.currency,
          });
        } catch {
          showError(ERROR_TEXT.currencyRateRefresh);
          setCurrency({
            currency: defaultCurrency,
          });
        }
      }
    }

    if (config.currency !== Currency.Eur) {
      getRates();
    } else {
      const { rate: cachedRate } = JSON.parse(
        localStorage.getItem(`${CurrencyLSKeys.RATE}${config.currency}`) ?? "{}"
      );

      setCurrency({
        rate: parseFloat(cachedRate || "1"),
      });
    }
  }, [config.currency]);

  const updateConfig = (newConfig: CustomerConfig) => {
    setConfig(newConfig);
    persistCurrencySettings({
      currency: newConfig.currency,
    });
  };

  if (loading) {
    return <Loader isBackground />;
  }

  return (
    <CustomerConfigContext.Provider value={{ config, update: updateConfig }}>
      {children}
    </CustomerConfigContext.Provider>
  );
}

export function useCustomerConfig() {
  const context = React.useContext(CustomerConfigContext);

  if (context === undefined) {
    throw new Error(
      "useCustomerConfig must be used within a CustomerConfigProvider"
    );
  }
  return context;
}

function setISOCurrencyFromString(
  currency: Maybe<string>,
  defaultCurrency = Currency.Eur
) {
  if (isISOCurrency(currency)) {
    return currency;
  }

  console.warn(
    `Wrong ISO currency ${currency}. Falling back to ${defaultCurrency}`
  );
  return defaultCurrency;
}

function isISOCurrency(currency: Maybe<string>): currency is Currency {
  return includes(Object.values(Currency), currency);
}
